import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import {
    FORM_KEYS,
    multiple_users_helpers_keys,
} from "repoV2/features/Common/modules/FormData/FormData.constants";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import {
    DEFAULT_GST_STATUS,
    DISCOUNT_OFF_TYPES,
    GST_STATUS,
    LISTING_TYPES,
} from "repoV2/features/Listing/Listing.constants";
import { getUnitLabelForPriceSummary } from "repoV2/features/Listing/utils/getUnitValueForPriceSummary";
import {
    DEFAULT_PAYMENT_CARD_TYPE,
    PAYMENT_CALCULATION_TYPE,
} from "repoV2/features/Payments/Payments.constants";
import { PAYMENT_PLAN_TYPE } from "repoV2/constants/listing";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { IMultipleUsersHelpers } from "repoV2/features/Common/modules/FormData/interfaces/MultipleUserHelpers.interfaces";
import { IVariant } from "repoV2/features/Listing/Listing.interfaces";
import { IPaymentDetails } from "repoV2/features/Common/modules/FormData/interfaces/PaymentDetails.interfaces";
import { ISelectedSessionDetails } from "repoV2/features/Common/modules/FormData/interfaces/SessionDetailsFormGroup.interfaces";
import {
    getAmountComponentsFromBasePrice,
    getDiscountAmountOnBasePrice,
    getListingPriceMultiplier,
} from "repoV2/features/Payments/utils/Payments.utils";
import { getSubscriptionShortDescription } from "repoV2/features/CheckoutPages/utils/getShortDescriptionHelpers";
import { IAppliedCouponCodeDetails } from "repoV2/features/Common/modules/FormData/interfaces/IAppliedCouponCodeDetails";
import { getActualBasePrice } from "repoV2/features/Listing/utils/getActualBasePrice";
import { getCurrencyLocaleValue } from "repoV2/features/Common/modules/Location/utils/getLocales";
import { checkIsListingClassesType } from "repoV2/features/CheckoutPages/utils/checkIsListingClassesType";
import { useCheckoutBasicDetailsContext } from "../../CheckoutBasicDetailsContextProvider";
import { useCheckoutPriceDetailsContext } from "../../CheckoutPriceDetailsContextProvider";
import { default_applied_coupon_code_details } from "../../PaymentDetailsSection/modules/FormGroups/modules/CouponCodeFormGroup/CouponCodeFormGroup.constants";
import { order_bumps_form_key } from "../../PaymentDetailsSection/modules/FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.constants";
import { IOrderBumpsFormValues } from "../../PaymentDetailsSection/modules/FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.interfaces";
import { IOrderBumpsItem } from "../PaymentSummarySection.interfaces";
import { getSelectedSessionDetailsFieldName } from "../../PaymentDetailsSection/modules/FormGroups/modules/SessionDetailsFormGroup/SessionDetailsFormGroup.utils";
import { multiple_user_count_key } from "../../PaymentDetailsSection/modules/CheckoutForm/CheckoutForm.constants";
import {
    selected_merchandise_quantity_form_key,
    selected_merchandise_variant_form_key,
} from "../../PaymentDetailsSection/modules/FormGroups/modules/MerchandiseDetailsFormGroup/MerchandiseDetailsFormGroup.constants";

// Todo: Move Payment Calculation to features/Payments

export const usePaymentSummarySection = () => {
    const { watch } = useFormContext();
    const {
        defaultListingTitle: baseListingTitle,
        listingUuid,
        listingType,
        paymentPlanType,
        courseMagnitudeChoice,
        courseMagnitudeCount,
        enableMultipleUserCountFlow,
        shippingDetails,
        isShiprocketEnabled,
    } = useCheckoutBasicDetailsContext();
    const { gstSummaryType: billingDisplayFormat, isInternationalCreator } =
        useCheckoutPriceDetailsContext();

    const priceDetailsSection = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.priceSection
    );

    const priceDetails = priceDetailsSection?.data;
    const priceDetailsIsFetched = priceDetailsSection?.isFetched;

    const sessionDetailsIsFetched = useSelector(
        (state: IRepoV2ReduxState) =>
            state.checkoutPages.listingDetails[listingUuid]?.sessionDetails
                ?.isFetched
    );

    const slots:
        | ISelectedSessionDetails["slots"]
        | ISelectedSessionDetails["batches"] =
        watch(
            getSelectedSessionDetailsFieldName({
                listingUuid,
                listingType,
            })
        ) || [];

    const allUserIds =
        useGetFormData<IMultipleUsersHelpers["allUserIds"]>({
            key: `${FORM_KEYS.multiple_users_helpers}.${multiple_users_helpers_keys.all_user_ids}`,
        }) || [];

    const multipleUsersHeadCount = watch(multiple_user_count_key);

    // Incase the multiple users count flow is active, then we need
    // to use the count from users counter, else the number of users
    // with the user details
    const customersCount =
        (enableMultipleUserCountFlow
            ? multipleUsersHeadCount
            : allUserIds.length) || 1;

    const variants =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseVariantsDetails?.data?.variants
        ) || {};

    const selectedMerchandiseVariant: IVariant =
        watch(selected_merchandise_variant_form_key) || {};

    const selectedMerchandiseQuantity =
        watch(selected_merchandise_quantity_form_key) || {};

    const shiprocketData =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.merchandiseShiprocketDetails?.data
        ) || {};
    const merchandiseQuantity = +selectedMerchandiseQuantity?.value || 1;

    // By default, payment will be calculated as one time payment.
    // Only in case of RC and Qpp, we give installments payment option
    const paymentCalculationType =
        useGetFormData<IPaymentDetails["paymentCalculationType"]>({
            key: `${FORM_KEYS.payment_details}.${FORM_KEYS.payment_calculation_type}`,
        }) || DEFAULT_PAYMENT_CARD_TYPE;

    const partPaymentDetails =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails[listingUuid]
                    ?.partPaymentDetails?.data
        ) || {};

    const { installmentsList } = partPaymentDetails;
    const firstInstallment = installmentsList?.[0] || {};

    const {
        installment_price_final: firstInstallmentPrice = 0,
        discount_applied: firstInstallmentDiscountAmount = 0,
    } = firstInstallment;

    // If coupon is applied, then the actual first installment price is
    // after deducting the first installment discount price from the first installment price
    const actualFirstInstallmentPrice =
        firstInstallmentPrice - firstInstallmentDiscountAmount;

    const {
        currency: currencySymbol = "", // TODO: make default based upon timezone
        currency_name: currencyName = "",
        gst_state: gstState = DEFAULT_GST_STATUS,
        internet_handling_fees: exlyIHFPercentage = 0,
        platform_gst: exlyGSTPercentage = 0,
        showInternationalInputFields = false,
    } = priceDetails || {};

    let {
        price_per_head: basePrice = 0, // price without any taxes, offers
        payable_amount: offerBasePrice = 0, // price with current offers(eg: LTO), without any taxes
        creator_gst: creatorTaxPercentage = 0, // GST / Tax in percentage of creator
    } = priceDetails || {};

    if (paymentCalculationType === PAYMENT_CALCULATION_TYPE.PART_PAYMENT) {
        // If payment calculation type is part payment, then we set both
        // basePrice and offerBasePrice as the actual first instalment price.
        // Reason being, that the final price from backend already handles
        // LTO scenerios. So, here basePrice and offerBasePrice is same.
        basePrice = actualFirstInstallmentPrice;
        offerBasePrice = actualFirstInstallmentPrice;
    }

    const isListingTypeMerchandiseWithVariants =
        listingType === LISTING_TYPES.MERCHANDISE &&
        Object.keys(variants).length;

    if (isListingTypeMerchandiseWithVariants) {
        // each variant has its own pricing, so we replace the price key with them
        basePrice = selectedMerchandiseVariant.price_per_head;
        offerBasePrice = selectedMerchandiseVariant.updated_price;
    }

    let merchandiseShippingValueWithTaxes = 0;

    if (!isShiprocketEnabled) {
        merchandiseShippingValueWithTaxes =
            (showInternationalInputFields
                ? shippingDetails?.shipping_charge_international
                : shippingDetails?.shipping_charge) || 0;
    } else {
        merchandiseShippingValueWithTaxes =
            shiprocketData?.value?.shippingCharge || 0;
    }

    // Incase of shiprocket, we always get the value from shiprocket only,
    // so regardless of our GST state, it is inclusive of GST
    // Incase of non shiprocket cases, backend gives the shipping charges
    // exclusive of GST in case of GST set as exclusive state
    if (gstState === GST_STATUS.EXCLUSIVE && !isShiprocketEnabled) {
        merchandiseShippingValueWithTaxes =
            (merchandiseShippingValueWithTaxes * (100 + creatorTaxPercentage)) /
                100 || 0;
    }

    const merchandiseShippingValueWithoutTaxes =
        getActualBasePrice(
            merchandiseShippingValueWithTaxes,
            creatorTaxPercentage
        ) || 0;

    // Selected sessions will go in an array, as in future there
    // will be multiple slots selection for a user
    let selectedSessions:
        | ISelectedSessionDetails["slots"]
        | ISelectedSessionDetails["batches"] = [];
    if (slots) {
        selectedSessions = slots;
    } // Todo: Handle this for Classes and Batches

    // For domestic user we use the word "GST", else use "Tax" here
    const wordUsedForTax = isInternationalCreator ? "Tax" : "GST";

    const listingTypeSpecificUnitLabel: string =
        getUnitLabelForPriceSummary(listingType);

    /**
    Steps to calculate the total price:
    1. The data we depend on backend for price is 
        a. basePrice (price_per_head - price user set during listing creation) and
        b. offerBasePrice (payable_amount - price inclusive of LTO)
    2. 
        a. if: GST_STATUS.INAPPLICABLE
            then: creator gst (creatorTaxPercentage) is 0 as gst is not applicable in this case.
        b. if: GST_STATUS.INCLUSIVE
            then: we remove the creator gst/tax amount from from both basePrice and offerBasePrice
    3. Call the util `getListingPriceMultiplier` which will give a multiplier value indicating
        the the number by which we will multiply our base price with, e.g. no. of slots in case of webinar.
    4. Get the price multiplier by multiplying the `listingPriceMultiplier` with customer count.
    5. Multiply both basePrice and offerBasePrice with multiplier and take the min value 
        as actual base price value.
    6. Call the util `getDiscountAmountOnBasePrice` to get the disount amount (if any).
    7. Subtract the discount amount from the amount calculated at step 5.
    8. Calculate the `totalAmount` after taking into account the creatorTaxPercentage, 
        exlyIHFPercentage and the exlyGSTPercentage (gst on ihf amount) 
     */

    if (gstState === GST_STATUS.INAPPLICABLE) {
        // if: gstState is of status Inapplicable, then gst is not applicable
        // so we make the creatorTaxPercentage as 0
        creatorTaxPercentage = 0;
    } else if (gstState === GST_STATUS.INCLUSIVE) {
        // if: base price from backend is inclusive of GST
        // then: calculate actual base price and use that for calculation everywhere
        // Note: This is also handling case of
        // `paymentCalculationType === PAYMENT_CALCULATION_TYPE.PART_PAYMENT && gstState === GST_STATUS.INCLUSIVE`
        // as well.
        basePrice = getActualBasePrice(basePrice, creatorTaxPercentage);
        offerBasePrice = getActualBasePrice(
            offerBasePrice,
            creatorTaxPercentage
        );
    }

    // In case of part payment, backend sends price inclusive of creatorTaxAmount.
    // So, in case of `gstState === GST_STATUS.EXCLUSIVE`, we remove the tax from the existing price first, then make further calculations
    // gstState === GST_STATUS.INCLUSIVE is already handled above
    if (
        paymentCalculationType === PAYMENT_CALCULATION_TYPE.PART_PAYMENT &&
        gstState === GST_STATUS.EXCLUSIVE
    ) {
        basePrice = getActualBasePrice(basePrice, creatorTaxPercentage);
        offerBasePrice = getActualBasePrice(
            offerBasePrice,
            creatorTaxPercentage
        );
    }

    // Based on the listing type and the number of slots
    // selected (for appointment and webinar), `listingPriceMultiplier`
    // is the number of units by which we multiply our basePrice with
    const listingPriceMultiplier = getListingPriceMultiplier({
        listingType,
        selectedSessions,
        listingQuantity: merchandiseQuantity,
    });

    // Final multiplier value dependent upon customer
    // count as there can be multiple customers for a single booking
    const priceMultiplier = customersCount * listingPriceMultiplier;

    const basePriceWMultiplier = basePrice * priceMultiplier;
    const basePriceWOffers = Math.min(basePrice, offerBasePrice);
    const basePriceWOffersNMultiplier = basePriceWOffers * priceMultiplier;

    // Selected order bumps, if any
    const selectedOrderBumps: IOrderBumpsFormValues =
        watch(order_bumps_form_key) || {};
    const orderBumpsArray: IOrderBumpsItem[] = [];

    let totalOrderBumpsValueWithTaxes = 0;
    let totalOrderBumpsValueWithoutTaxes = 0;
    let totalOrderBumpsIHFValue = 0;
    let totalOrderBumpsIHFValueWithExlyTaxes = 0;

    // We iterate over each of the order bumps, to create an 'orderBumpsArray'
    // containing the necessary information we need to render in payment summary
    Object.values(selectedOrderBumps).forEach(orderBump => {
        const {
            listingDetails: orderBumpListingDetails,
            selectedQuantity: orderBumpSelectedQuantity,
        } = orderBump;
        // in case of merchandise we can have multiple items
        const currentQuantity = orderBumpSelectedQuantity || 1;

        // Backend gives 'updated_price' key, containing tax irrespective of gst state inclusive or exclusive
        const currentValue = orderBumpListingDetails.updated_price || 0;
        const currentTaxPercentage =
            (gstState === GST_STATUS.INAPPLICABLE
                ? 0
                : orderBumpListingDetails.creator_gst) || 0;
        const currentValueWithoutTaxes = getActualBasePrice(
            currentValue,
            currentTaxPercentage
        );
        const currentIHFPercentage =
            orderBumpListingDetails.internet_handling_fees || 0;
        const currentExlyGSTPercentage =
            orderBumpListingDetails.platform_gst || 0;

        // Shipping to exist for merchandise order bumps
        const isShippingValueExist =
            orderBumpListingDetails.shipping_details?.shipment_method;
        let shippingValueWithTaxes =
            orderBumpListingDetails.shipping_details?.shipping_charge || 0;
        if (showInternationalInputFields) {
            shippingValueWithTaxes =
                orderBumpListingDetails?.shipping_details
                    ?.shipping_charge_international || 0;
        }
        const shippingValueWithoutTaxes = getActualBasePrice(
            shippingValueWithTaxes,
            currentTaxPercentage || 0
        );
        const listingData: IOrderBumpsItem = {
            listingUuid: orderBumpListingDetails.additional_listing_uid,
            title: orderBumpListingDetails.title || "",
            finalPriceWithTaxes: currentValue * currentQuantity,
            finalPriceWithoutTaxes: currentValueWithoutTaxes * currentQuantity,
            quantity: currentQuantity,
            ...(isShippingValueExist && {
                shippingPriceWithTaxes: shippingValueWithTaxes,
                shippingPriceWithoutTaxes: shippingValueWithoutTaxes,
            }),
        };

        // Changing the totalOrderBumpValues
        totalOrderBumpsValueWithTaxes += currentValue * currentQuantity;
        totalOrderBumpsValueWithoutTaxes +=
            currentValueWithoutTaxes * currentQuantity;

        totalOrderBumpsIHFValue +=
            (currentValue * currentQuantity * currentIHFPercentage) / 100;
        totalOrderBumpsIHFValueWithExlyTaxes +=
            (((currentValue * currentQuantity * currentIHFPercentage) / 100) *
                currentExlyGSTPercentage) /
            100;

        if (isShippingValueExist) {
            totalOrderBumpsValueWithTaxes += shippingValueWithTaxes;
            totalOrderBumpsValueWithoutTaxes += shippingValueWithoutTaxes;
        }
        orderBumpsArray.push(listingData);
    });

    // Tax amount is the value with tax minus the value without tax
    const totalOrderBumpsTaxAmount =
        totalOrderBumpsValueWithTaxes - totalOrderBumpsValueWithoutTaxes;

    let discountAmount = 0;
    const appliedCouponCodeData =
        useGetFormData<IAppliedCouponCodeDetails>({
            key: `${FORM_KEYS.listing_details}.${listingUuid}.${FORM_KEYS.applied_coupon_code_details}`,
        }) || default_applied_coupon_code_details;
    const discountOffType =
        appliedCouponCodeData.discountOffType || DISCOUNT_OFF_TYPES.ABSOLUTE;
    const discountIncludesGst = appliedCouponCodeData.includesGst || false;
    const discountValue = appliedCouponCodeData.value || 0;

    // Incase of part payments scenerio, discount is already handled in the
    // first installment price
    if (paymentCalculationType === PAYMENT_CALCULATION_TYPE.ONE_TIME_PAYMENT) {
        if (appliedCouponCodeData.applied) {
            discountAmount = getDiscountAmountOnBasePrice({
                // Note: here the discount component does not include order bumps amount,
                // as we only need to apply the discount on the base offering
                basePrice: basePriceWOffersNMultiplier,
                discountOffType,
                discountValue,
                discountIncludesGst,
                creatorTaxPercentage,
            });
        }
    }

    const priceWOffersNDiscounts = basePriceWOffersNMultiplier - discountAmount;

    const shippingTaxAmount =
        merchandiseShippingValueWithTaxes -
        merchandiseShippingValueWithoutTaxes;

    /*
    STARTS - amount that the user will be paying
    */
    const { creatorTaxAmount, exlyIHFAmount, exlyTaxAmount, totalAmount } =
        getAmountComponentsFromBasePrice({
            basePrice: priceWOffersNDiscounts,
            creatorTaxPercentage,
            exlyIHFPercentage,
            exlyGSTPercentage,
            totalOrderBumpsValueWithTaxes,
            totalOrderBumpsIHFValue,
            totalOrderBumpsIHFValueWithExlyTaxes,
            shippingValueWithTaxes: merchandiseShippingValueWithTaxes,
        });

    /*
    ENDS - amount that the user will be paying
    */

    let paymentSummarySubHeading = "";
    let paymentSummaryHeading = "Grand Total";
    if (
        (listingType === LISTING_TYPES.NO_SCHEDULE ||
            checkIsListingClassesType({ listingType })) && // it is a qpp listing, or a class
        paymentPlanType === PAYMENT_PLAN_TYPE.SUBSCRIPTIONS && // payment is of subscription type
        Object.keys(selectedOrderBumps).length < 1 // No order Bump exist
    ) {
        paymentSummaryHeading = "Subscription Amount";
        paymentSummarySubHeading = getSubscriptionShortDescription({
            courseMagnitudeCount,
            courseMagnitudeChoice,
        });
    }

    const currencyLocale = getCurrencyLocaleValue(currencyName);

    const showShippingCharges =
        listingType === LISTING_TYPES.MERCHANDISE &&
        (!isShiprocketEnabled ||
            (shiprocketData?.isVerified && !shiprocketData.error));

    const commonPropsOfPaymentSummaryTypes = {
        baseListingTitle,

        creatorTaxPercentage,
        exlyIHFPercentage,
        exlyGSTPercentage,

        listingPriceMultiplier,
        customersCount,

        basePrice,
        basePriceWOffers,
        basePriceWMultiplier,
        basePriceWOffersNMultiplier,

        exlyIHFAmount,
        exlyTaxAmount,

        totalAmount,

        discountOffType,
        discountIncludesGst,
        discountAmount,

        currencySymbol,
        currencyLocale,

        listingTypeSpecificUnitLabel,

        paymentSummaryHeading,

        orderBumpsArray,
        totalOrderBumpsValueWithTaxes,
        totalOrderBumpsValueWithoutTaxes,
        totalOrderBumpsTaxAmount,
        paymentSummarySubHeading,

        shippingValueWithTaxes: merchandiseShippingValueWithTaxes,
        shippingValueWithoutTaxes: merchandiseShippingValueWithoutTaxes,
        shippingTaxAmount,

        showShippingCharges,
    };

    return {
        isFetched: sessionDetailsIsFetched && priceDetailsIsFetched,
        billingDisplayFormat,
        commonPropsOfPaymentSummaryTypes,
        creatorTaxAmount,
        wordUsedForTax,
        currencySymbol,
    };
};
